(function () {
  if (window.Eurus?.loadedScript) {
    window.Eurus.loadedScript.add('b2b-registration-form.js');
  }

  const MAX_FILE_SIZE_MB = 5;
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
  ];
  const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

  const MESSAGES = {
    required: 'Dieses Feld ist ein Pflichtfeld.',
    email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    fileSize: 'Die Dateigröße ist zu groß (maximal 5 MB).',
    fileType: 'Bitte laden Sie eine PDF-, JPG- oder PNG-Datei hoch.',
  };

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function isAllowedFile(file) {
    if (ALLOWED_FILE_TYPES.includes(file.type)) return true;
    const name = file.name.toLowerCase();
    return ALLOWED_FILE_EXTENSIONS.some((ext) => name.endsWith(ext));
  }

  function formatFileSize(file) {
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb < 1) {
      return `${Math.max(1, Math.round(file.size / 1024))} KB`;
    }
    return `${sizeMb.toFixed(1).replace('.', ',')} MB`;
  }

  function formatFileType(file) {
    if (file.type === 'application/pdf') return 'PDF';
    if (file.type.startsWith('image/')) return file.type.split('/')[1].toUpperCase();
    const ext = file.name.split('.').pop();
    return ext ? ext.toUpperCase() : 'DATEI';
  }

  function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function getScrollOffset() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    const headerHeight = parseInt(raw, 10);
    return (Number.isFinite(headerHeight) ? headerHeight : 80) + 24;
  }

  function scrollToTarget(element, behavior = 'smooth') {
    if (!element) return;

    const top = window.scrollY + element.getBoundingClientRect().top - getScrollOffset();
    window.scrollTo({ top: Math.max(0, top), behavior });
  }

  class B2BRegistrationForm {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('.b2b-registration-form__form');
      this.fields = {};
      this.file = null;
      this.fileBase64 = null;
      this.fileBase64Ready = true;
      this.messageMinLength = parseInt(root.dataset.messageMinLength || '10', 10);
      this.submitEndpoint = (root.dataset.submitEndpoint || '').trim();
      this.submitButton = this.form?.querySelector('[data-b2b-submit]');
      this.errorPanel = root.querySelector('[data-b2b-error-panel]');

      if (!this.form) return;

      this.form.setAttribute('novalidate', '');
      this.cacheFields();
      this.initFileUpload();
      this.bindEvents();
    }

    cacheFields() {
      this.fields = {
        anrede: this.form.querySelector('[data-field="anrede"]'),
        titel: this.form.querySelector('[data-field="titel"]'),
        vorname: this.form.querySelector('[data-field="vorname"]'),
        nachname: this.form.querySelector('[data-field="nachname"]'),
        firma: this.form.querySelector('[data-field="firma"]'),
        strasse: this.form.querySelector('[data-field="strasse"]'),
        plzOrt: this.form.querySelector('[data-field="plz_ort"]'),
        email: this.form.querySelector('[data-field="email"]'),
        telefon: this.form.querySelector('[data-field="telefon"]'),
        nachricht: this.form.querySelector('[data-field="nachricht"]'),
        fileInput: this.form.querySelector('[data-field="apothekenbescheinigung"]'),
        newsletter: this.form.querySelector('[data-field="newsletter"]'),
      };
    }

    bindEvents() {
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));

      Object.values(this.fields).forEach((field) => {
        if (!field || field === this.fields.fileInput) return;
        field.addEventListener('blur', () => this.validateField(field));
        field.addEventListener('input', () => {
          if (field.closest('[data-field-wrapper]')?.classList.contains('b2b-field--error')) {
            this.validateField(field);
          }
        });
        field.addEventListener('change', () => {
          if (field.closest('[data-field-wrapper]')?.classList.contains('b2b-field--error')) {
            this.validateField(field);
          }
        });
      });
    }

    initFileUpload() {
      const wrap = this.form.querySelector('[data-file-upload]');
      if (!wrap || !this.fields.fileInput) return;

      const dropZone = wrap.querySelector('.drop-zone');
      const removeButton = wrap.querySelector('[data-file-remove]');

      dropZone?.addEventListener('click', () => this.fields.fileInput.click());

      this.fields.fileInput.addEventListener('change', () => {
        const selectedFile = this.fields.fileInput.files?.[0];
        if (!selectedFile) return;
        this.setFile(selectedFile);
      });

      removeButton?.addEventListener('click', (event) => {
        event.preventDefault();
        this.clearFile();
      });

      dropZone?.addEventListener('dragover', (event) => {
        event.preventDefault();
        dropZone.classList.add('drop-zone--over');
      });

      ['dragleave', 'dragend'].forEach((type) => {
        dropZone?.addEventListener(type, () => dropZone.classList.remove('drop-zone--over'));
      });

      dropZone?.addEventListener('drop', (event) => {
        event.preventDefault();
        dropZone.classList.remove('drop-zone--over');
        const droppedFile = event.dataTransfer.files?.[0];
        if (!droppedFile) return;
        this.setFile(droppedFile);
      });
    }

    updateSubmitButtonState() {
      if (!this.submitButton) return;

      const fileLoading = this.file && !this.fileBase64Ready;
      this.submitButton.disabled = fileLoading;
      this.submitButton.setAttribute('aria-busy', fileLoading ? 'true' : 'false');
    }

    cacheFileBase64(file) {
      this.fileBase64 = null;
      this.fileBase64Ready = false;
      this.updateSubmitButtonState();

      readFileAsBase64(file)
        .then((base64) => {
          if (this.file !== file) return;
          this.fileBase64 = base64;
          this.fileBase64Ready = true;
          this.updateSubmitButtonState();
        })
        .catch(() => {
          if (this.file !== file) return;
          this.fileBase64 = null;
          this.fileBase64Ready = true;
          this.updateSubmitButtonState();
        });
    }

    setFile(file) {
      const wrap = this.form.querySelector('[data-file-upload]');
      if (!wrap) return;

      if (!isAllowedFile(file)) {
        this.showFieldError(this.fields.fileInput, MESSAGES.fileType);
        this.clearFile(false);
        return;
      }

      if (file.size / (1024 * 1024) > MAX_FILE_SIZE_MB) {
        this.showFieldError(this.fields.fileInput, MESSAGES.fileSize);
        this.clearFile(false);
        return;
      }

      this.file = file;
      this.clearFieldError(this.fields.fileInput);
      this.cacheFileBase64(file);

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      this.fields.fileInput.files = dataTransfer.files;

      wrap.classList.add('drop-zone-wrap--inactive');

      const title = wrap.querySelector('.dd-file-info__title');
      const type = wrap.querySelector('.dd-file-info__type');
      const preview = wrap.querySelector('.dd-thumbnail');
      const previewIcon = wrap.querySelector('.icon-file');

      if (title) {
        title.textContent = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      }
      if (type) {
        type.textContent = `${formatFileType(file)} • ${formatFileSize(file)}`;
      }

      if (preview && previewIcon) {
        preview.removeAttribute('style');
        previewIcon.classList.add('hidden');

        if (/\.(jpe?g|png)$/i.test(file.name)) {
          const reader = new FileReader();
          reader.onload = () => {
            preview.setAttribute('style', `background-image:url("${reader.result}");`);
          };
          reader.readAsDataURL(file);
        } else {
          previewIcon.classList.remove('hidden');
        }
      }
    }

    clearFile(resetInput = true) {
      const wrap = this.form.querySelector('[data-file-upload]');
      this.file = null;
      this.fileBase64 = null;
      this.fileBase64Ready = true;

      if (resetInput && this.fields.fileInput) {
        this.fields.fileInput.value = '';
      }

      wrap?.classList.remove('drop-zone-wrap--inactive', 'drop-zone-wrap--error');
      this.updateSubmitButtonState();
    }

    getWrapper(field) {
      return field?.closest('[data-field-wrapper]') || null;
    }

    showFieldError(field, message) {
      const wrapper = this.getWrapper(field);
      if (!wrapper) return;

      wrapper.classList.add('b2b-field--error');
      field.setAttribute('aria-invalid', 'true');

      let errorEl = wrapper.querySelector('[data-field-error]');
      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.setAttribute('data-field-error', '');
        errorEl.className = 'b2b-field-error mt-1 text-sm text-[rgba(var(--color-error))]';
        wrapper.appendChild(errorEl);
      }

      errorEl.textContent = message;
    }

    clearFieldError(field) {
      const wrapper = this.getWrapper(field);
      if (!wrapper) return;

      wrapper.classList.remove('b2b-field--error');
      field.removeAttribute('aria-invalid');
      wrapper.querySelector('[data-field-error]')?.remove();
    }

    validateField(field) {
      if (!field) return true;

      const name = field.dataset.field;
      const value = field.value.trim();

      if (name === 'apothekenbescheinigung') {
        if (this.file && !isAllowedFile(this.file)) {
          this.showFieldError(field, MESSAGES.fileType);
          return false;
        }
        if (this.file && this.file.size / (1024 * 1024) > MAX_FILE_SIZE_MB) {
          this.showFieldError(field, MESSAGES.fileSize);
          return false;
        }
        this.clearFieldError(field);
        return true;
      }

      const requiredFields = ['firma', 'strasse', 'plz_ort', 'email'];
      if (requiredFields.includes(name) && !value) {
        this.showFieldError(field, MESSAGES.required);
        return false;
      }

      if (name === 'email' && value && !isValidEmail(value)) {
        this.showFieldError(field, MESSAGES.email);
        return false;
      }

      if (name === 'nachricht' && value && this.messageMinLength > 0 && value.length < this.messageMinLength) {
        this.showFieldError(
          field,
          `Die Nachricht muss mindestens ${this.messageMinLength} Zeichen lang sein.`
        );
        return false;
      }

      this.clearFieldError(field);
      return true;
    }

    validateForm() {
      let isValid = true;
      let firstInvalidField = null;

      [
        this.fields.firma,
        this.fields.strasse,
        this.fields.plzOrt,
        this.fields.email,
        this.fields.nachricht,
        this.fields.fileInput,
      ].forEach((field) => {
        if (!field) return;
        const fieldValid = this.validateField(field);
        if (!fieldValid) {
          isValid = false;
          if (!firstInvalidField) firstInvalidField = field;
        }
      });

      if (firstInvalidField) {
        firstInvalidField.focus();
      }

      return isValid;
    }

    collectFormData() {
      const data = {
        formular: 'B2B-Registrierung',
        submittedAt: new Date().toISOString(),
      };

      this.form.querySelectorAll('[data-field]').forEach((element) => {
        if (element.type === 'file' || element.type === 'checkbox') return;

        const fieldKey = element.dataset.field;
        if (!fieldKey || fieldKey === 'apothekenbescheinigung') return;

        data[fieldKey] = typeof element.value === 'string' ? element.value.trim() : element.value;
      });

      data.newsletter = Boolean(this.fields.newsletter?.checked);

      const nameParts = [data.anrede, data.titel, data.vorname, data.nachname].filter(Boolean);
      data.name = nameParts.join(' ').trim() || data.firma || '';

      data.apothekenbescheinigung = null;

      if (this.file) {
        data.apothekenbescheinigung = {
          name: this.file.name,
          type: this.file.type,
          size: this.file.size,
          base64: this.fileBase64 || null,
        };
      }

      return data;
    }

    showSuccessPanel() {
      const panel = this.root.querySelector('[data-b2b-success-panel]');
      if (!panel || !this.form) return;

      this.hideErrorPanel();
      this.form.hidden = true;
      panel.hidden = false;
      panel.classList.remove('hidden');

      window.requestAnimationFrame(() => {
        scrollToTarget(panel, 'smooth');
        panel.focus({ preventScroll: true });
      });
    }

    showErrorPanel() {
      if (!this.errorPanel) return;

      this.errorPanel.hidden = false;
      this.errorPanel.classList.remove('hidden');

      window.requestAnimationFrame(() => {
        scrollToTarget(this.errorPanel, 'smooth');
        this.errorPanel.focus({ preventScroll: true });
      });
    }

    hideErrorPanel() {
      if (!this.errorPanel) return;

      this.errorPanel.hidden = true;
      this.errorPanel.classList.add('hidden');
    }

    setSubmitting(isSubmitting) {
      if (!this.submitButton) return;

      this.submitButton.disabled = isSubmitting;
      this.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
    }

    async submitToEndpoint(payload) {
      const response = await fetch(this.submitEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
    }

    handleSubmit(event) {
      event.preventDefault();

      if (!this.validateForm()) {
        return;
      }

      if (this.file && !this.fileBase64Ready) {
        return;
      }

      const payload = this.collectFormData();

      if (!this.submitEndpoint) {
        console.log('[B2B-Registrierung]', payload);
        this.setSubmitting(true);
        this.showSuccessPanel();
        return;
      }

      this.hideErrorPanel();
      this.setSubmitting(true);

      this.submitToEndpoint(payload)
        .then(() => {
          this.showSuccessPanel();
        })
        .catch((error) => {
          console.error('[B2B-Registrierung] Submit failed:', error);
          this.showErrorPanel();
        })
        .finally(() => {
          this.setSubmitting(false);
        });
    }
  }

  function initForms() {
    document.querySelectorAll('[data-b2b-registration-root]').forEach((root) => {
      if (root.dataset.b2bRegistrationInit === 'true') return;
      root.dataset.b2bRegistrationInit = 'true';
      new B2BRegistrationForm(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initForms);
  } else {
    initForms();
  }

  document.addEventListener('shopify:section:load', initForms);
})();
